import React, { useState } from 'react';
import styled from 'styled-components';
import CreatableSelect from 'components/CreatableSelect';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const DemoRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 280px;
  }
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const CreatableSelectDocs = () => {
  const [basicValue, setBasicValue] = useState(null);
  const [creatableValue, setCreatableValue] = useState(null);
  const [loadingValue, setLoadingValue] = useState(null);
  const [customOptions, setCustomOptions] = useState([
    { value: 'company-1', label: 'ABC Insurance' },
    { value: 'company-2', label: 'XYZ Insurance Co' },
    { value: 'company-3', label: 'Global Insurance Group' },
  ]);

  const sampleOptions = [
    { value: 'option-1', label: 'Option One' },
    { value: 'option-2', label: 'Option Two' },
    { value: 'option-3', label: 'Option Three' },
    { value: 'option-4', label: 'Option Four' },
  ];

  const handleCreateOption = async (inputValue) => {
    // Simulate async creation
    await new Promise((resolve) => setTimeout(resolve, 500));
    const newOption = {
      value: `company-${Date.now()}`,
      label: inputValue,
    };
    setCustomOptions((prev) => [...prev, newOption]);
    return newOption;
  };

  const props = [
    {
      name: 'options',
      type: 'array',
      required: false,
      description:
        'Array of option objects. Each object should have at least value and label properties.',
    },
    {
      name: 'value',
      type: 'object',
      required: false,
      description: 'Currently selected option object',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description: 'Callback when an option is selected: (option) => void',
    },
    {
      name: 'onCreateOption',
      type: 'function',
      required: false,
      description:
        'Async callback to create a new option: (inputValue) => Promise<option>. If provided, enables create functionality.',
    },
    {
      name: 'placeholder',
      type: 'string',
      required: false,
      description: 'Placeholder text when no option is selected',
    },
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label displayed above the select',
    },
    {
      name: 'disabled',
      type: 'boolean',
      required: false,
      description: 'Disable the select (default: false)',
    },
    {
      name: 'loading',
      type: 'boolean',
      required: false,
      description: 'Show loading spinner in dropdown (default: false)',
    },
    {
      name: 'error',
      type: 'string',
      required: false,
      description: 'Error message to display below the select',
    },
    {
      name: 'searchable',
      type: 'boolean',
      required: false,
      description: 'Enable search/filter functionality (default: true)',
    },
    {
      name: 'createLabel',
      type: 'string',
      required: false,
      description: 'Label for create option button (default: "Create")',
    },
    {
      name: 'noOptionsMessage',
      type: 'string',
      required: false,
      description:
        'Message when no options match search (default: "No options found")',
    },
    {
      name: 'formatCreateLabel',
      type: 'function',
      required: false,
      description:
        'Custom function to format the create option label: (inputValue) => string',
    },
    {
      name: 'getOptionLabel',
      type: 'function',
      required: false,
      description:
        'Function to get label from option object (default: (option) => option.label)',
    },
    {
      name: 'getOptionValue',
      type: 'function',
      required: false,
      description:
        'Function to get value from option object (default: (option) => option.value)',
    },
  ];

  return (
    <>
      <PageTitle>Creatable Select</PageTitle>
      <PageDescription>
        A searchable dropdown component that allows users to select from
        existing options or create new ones. Perfect for scenarios like
        selecting a company from a list while also allowing users to add new
        companies on the fly.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Select</SectionTitle>
        <ComponentDemo
          title="Simple select without create functionality"
          code={`import CreatableSelect from 'components/CreatableSelect';

const [value, setValue] = useState(null);

const options = [
  { value: 'option-1', label: 'Option One' },
  { value: 'option-2', label: 'Option Two' },
  { value: 'option-3', label: 'Option Three' },
];

<CreatableSelect
  options={options}
  value={value}
  onChange={setValue}
  placeholder="Select an option..."
  label="Basic Select"
/>`}
        >
          <DemoRow>
            <div>
              <CreatableSelect
                options={sampleOptions}
                value={basicValue}
                onChange={setBasicValue}
                placeholder="Select an option..."
                label="Basic Select"
              />
              {basicValue && (
                <SelectedValue>
                  Selected: <code>{JSON.stringify(basicValue)}</code>
                </SelectedValue>
              )}
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Creatable Select</SectionTitle>
        <ComponentDemo
          title="Select with create new option functionality"
          code={`import CreatableSelect from 'components/CreatableSelect';

const [value, setValue] = useState(null);
const [options, setOptions] = useState([
  { value: 'company-1', label: 'ABC Insurance' },
  { value: 'company-2', label: 'XYZ Insurance Co' },
]);

const handleCreateOption = async (inputValue) => {
  // API call to create new company
  const newOption = await createCompany(inputValue);
  setOptions(prev => [...prev, newOption]);
  return newOption;
};

<CreatableSelect
  options={options}
  value={value}
  onChange={setValue}
  onCreateOption={handleCreateOption}
  placeholder="Select or create company..."
  label="Insurance Company"
  createLabel="Add new company"
/>`}
        >
          <DemoRow>
            <div>
              <CreatableSelect
                options={customOptions}
                value={creatableValue}
                onChange={setCreatableValue}
                onCreateOption={handleCreateOption}
                placeholder="Select or create company..."
                label="Insurance Company"
                createLabel="Add new company"
              />
              {creatableValue && (
                <SelectedValue>
                  Selected: <code>{JSON.stringify(creatableValue)}</code>
                </SelectedValue>
              )}
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Loading State</SectionTitle>
        <ComponentDemo
          title="Select with loading state"
          code={`<CreatableSelect
  options={[]}
  value={value}
  onChange={setValue}
  loading={true}
  placeholder="Loading options..."
  label="Loading Select"
/>`}
        >
          <DemoRow>
            <div>
              <CreatableSelect
                options={[]}
                value={loadingValue}
                onChange={setLoadingValue}
                loading={true}
                placeholder="Loading options..."
                label="Loading Select"
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Error</SectionTitle>
        <ComponentDemo
          title="Select with error message"
          code={`<CreatableSelect
  options={options}
  value={null}
  onChange={setValue}
  placeholder="Select an option..."
  label="Required Field"
  error="This field is required"
/>`}
        >
          <DemoRow>
            <div>
              <CreatableSelect
                options={sampleOptions}
                value={null}
                onChange={setBasicValue}
                placeholder="Select an option..."
                label="Required Field"
                error="This field is required"
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Disabled State</SectionTitle>
        <ComponentDemo
          title="Disabled select"
          code={`<CreatableSelect
  options={options}
  value={{ value: 'option-1', label: 'Option One' }}
  onChange={setValue}
  disabled={true}
  label="Disabled Select"
/>`}
        >
          <DemoRow>
            <div>
              <CreatableSelect
                options={sampleOptions}
                value={{ value: 'option-1', label: 'Option One' }}
                onChange={undefined}
                disabled={true}
                label="Disabled Select"
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import CreatableSelect from 'components/CreatableSelect';
// or
import { CreatableSelect } from 'components/CreatableSelect';`}
        />
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        {/* eslint-disable jsx-a11y/accessible-emoji */}
        <ul style={{ lineHeight: 1.8 }}>
          <li>🎨 Fully theme-aware (light/dark mode support)</li>
          <li>🔍 Searchable dropdown with filtering</li>
          <li>➕ Create new options on the fly</li>
          <li>⏳ Loading state support</li>
          <li>❌ Error state display</li>
          <li>⌨️ Keyboard support (Escape to close)</li>
          <li>🖱️ Click outside to close</li>
          <li>📱 Mobile-friendly design</li>
          <li>🔄 Async create option support</li>
        </ul>
        {/* eslint-enable jsx-a11y/accessible-emoji */}
      </Section>

      <Section>
        <SectionTitle>Usage with GraphQL</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import { useQuery, useMutation } from '@apollo/client';
import CreatableSelect from 'components/CreatableSelect';
import GET_COMPANIES from './gql/getCompanies';
import CREATE_COMPANY from './gql/createCompany';

const CompanySelect = ({ value, onChange }) => {
  const { data, loading } = useQuery(GET_COMPANIES);
  const [createCompany] = useMutation(CREATE_COMPANY, {
    refetchQueries: [{ query: GET_COMPANIES }],
  });

  const options = data?.getCompanies?.map(company => ({
    value: company.id,
    label: company.name,
  })) || [];

  const handleCreate = async (name) => {
    const { data } = await createCompany({
      variables: { input: { name } },
    });
    return {
      value: data.createCompany.id,
      label: data.createCompany.name,
    };
  };

  return (
    <CreatableSelect
      options={options}
      value={value}
      onChange={onChange}
      onCreateOption={handleCreate}
      loading={loading}
      label="Company"
      placeholder="Select or create company..."
    />
  );
};`}
        />
      </Section>
    </>
  );
};

export default CreatableSelectDocs;
